import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login } from "../../services/login";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import classNames from "classnames";
import { notify, resetSecureData, setSecureData } from "../../helpers";
import { Eye, EyeOff, Loader, Lock } from "lucide-react";
import { useDispatch } from "react-redux";
import { setPassword, setUsername } from "../../redux/actions";
import Spacing from "../Spacing";
import {
  setAccessToken,
  setRefreshToken,
} from "../../Session";

export default function Login() {
  const now = new Date();
  const currentYear = now.getFullYear();

  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [loginPassword, setLoginPassword] = useState(false);
  const [validateInput, setValidateInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch()
  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    localStorage.clear();
    await resetSecureData();
    const checkEmpty = Object.values(credentials).some((x) => x === "");
    if (checkEmpty) {
      setValidateInput(true);
      return;
    }

    setLoading(true);

    try {
      const { data } = await login(credentials);
      if(data?.account_type === "charting") {
        notify("error", `This account doesn’t have access to the web portal. For assistance, please contact support@doc.pro`, 10);
        return;
      }
      if (data?.access_token) {
        await setSecureData("accessToken", data?.access_token);
        await setSecureData("refreshToken", data?.refresh_token);
        setAccessToken(data?.access_token);
        setRefreshToken(data?.refresh_token);
        navigate("/");
      } else if (data?.message == "Success") {
        dispatch(setUsername(credentials.username))
        dispatch(setPassword(credentials.password))
        navigate('/otp-method')

      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Oops! Server throws error";
      
      if (errorMessage.includes("Password Expired for User")) {

        notify("error", "Password expired. Redirecting to reset password...", 0.5);
        // Redirect to the reset password page
        // navigate("/reset-password", { state: { username: credentials.username } });
        setTimeout(() => {
          navigate("/reset-password");
        }, 3000);
      } else {
        notify("error", errorMessage, 0.5);
      }
    } finally {
      setLoading(false);
    }
  };

  // http://localhost:3000/login?auth=dXNlciwxMjM0
  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const loginAuth = searchParams.get("auth");

      if (loginAuth) {
        setLoading(true);
        AuthDecodeFunc(loginAuth);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const AuthDecodeFunc = async (loginAuth) => {
    try {
      const decodedString = atob(loginAuth);
      const [auth_user, auth_pass] = decodedString.split(',');

      if (auth_user !== undefined && auth_pass !== undefined) {
        setCredentials({
          username: auth_user,
          password: auth_pass
        });

        setValidateInput(true);

        try {
          const { data } = await login({ username: auth_user, password: auth_pass }); 
          
          await setSecureData("accessToken", data?.access_token);
          await setSecureData("refreshToken", data?.refresh_token);
          setAccessToken(data?.access_token);
          setRefreshToken(data?.refresh_token);
          navigate("/");
        } catch (error) {
          const errorMessage =
            error?.response?.data?.errorMessage ||
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Oops! Server throws error";
          notify("error", errorMessage, 0.5);
        }
      }
    } catch (e) {}     
    setLoading(false);
  };

  useDocumentTitle("Login");

  const [currentImage, setCurrentImage] = useState(0);
  const [fade, setFade] = useState(false);
  const images = [
    "/images/login_img.svg",
    "/images/login_img2.svg",
    "/images/login_img3.svg",
  ];

  useEffect(() => {
    const changeImage = () => {
      setFade(true); // start fade out
      setTimeout(() => {
        setCurrentImage((prevImage) => (prevImage + 1) % images.length);
        setFade(false); // fade back in
      }, 500); // duration should match CSS transition
    };
  
    const intervalId = setInterval(changeImage, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="docpro_login_wrap">
      <div
      className={`docpro_login_thumb ${fade ? "fade" : ""}`}
      style={{
        backgroundImage: `linear-gradient(to right, rgba(255,255,255,0) 80%, rgba(255,255,255,1) 100%), url(${images[currentImage]})`,
      }}
    ></div>
      <div className="docpro_login_right">
        <div className="docpro_login_card_wrap">
          <div className="docpro_login_card">
            <div className="docpro_text_center">
              <img src="/images/logo_2.svg" alt="Logo" />
              <Spacing lg="32" sm="32" />
              {/* <h1 className="docpro_mb_0">Hello, welcome back!</h1> */}
            </div>
            <Spacing lg="20" sm="20" />

            <form onSubmit={handleSignIn}>
              <label className="docpro_fw_500 docpro_primary_color">
                Username
              </label>
              <input
                type="text"
                className={classNames("docpro_form_field size_lg", {
                  danger_alert: validateInput && credentials.username === "",
                })}
                placeholder="Enter your username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
              />
              <div className="docpro_height_lg_20 docpro_height_20 docpro_label_group" style={{marginTop: "29px", marginBottom: "5px"}}>
                <label className="docpro_fw_500 docpro_primary_color">
                  Password
                </label>
                <Link to="/forgot-password" className="docpro_link_btn">
                  Forgot password?
                </Link>
              </div>
              <div className="docpro_form_field_with_icon">
                <input
                  name="password"
                  type={loginPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className={classNames("docpro_form_field size_lg", {
                    danger_alert: validateInput && credentials.password === "",
                  })}
                  value={credentials.password}
                  onChange={handleChange}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                { loginPassword ?
                  <Eye onClick={()=>setLoginPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                :
                  <EyeOff onClick={()=>setLoginPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                }
              </div>
              <Spacing lg="20" sm="20" />

              <button
                className={`width_100 docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: `${loading ? "none" : "initial"}` }}
                type="submit"
              >
                Sign in
                {loading && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </form>
            <Spacing lg="16" sm="16" />
            {/* <div className="docpro_text_center">
              <Link to="/" className="docpro_link_btn">
                Forgot password?
              </Link>
            </div> */}
          </div>
        </div>
        <footer className="docpro_login_footer">
          <div className="docpro_login_footer_nav">
            <Link to="/">Privacy policy</Link>
            <Link to="/">Terms & conditions</Link>
            <Link to="/">Terms of service</Link>
            <Link to="/">HIPAA Compliance</Link>
          </div>
          <p>© {currentYear} DocPro. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
